import { createSlice } from "@reduxjs/toolkit";
import { transformObjectToKeyValueArray } from "../../../utilities/commonUtilities";
import { defaultTubingWeight } from "../../../constants/jetlift/jetlift";
import { tubingTab, units } from "../../model/jetlift/jetlift";

interface TubingState {
    loading: boolean;
    error: string | undefined;
    tubingInput: tubingTab | null;
    catalogType: number | string;
    units: units | null;
}

export const initialState: TubingState = {
    loading: false,
    error: "",
    tubingInput: null,
    catalogType: 0,
    units: null,
};

const TubingSlice = createSlice({
    name: "jetlift/tubing",
    initialState,
    reducers: {
        resetTubingTab: () => ({
            ...initialState
        }),
        setInputUnits: (state, action) => {
            state.units = action?.payload;
        },
        setTubingDetails: (state, action) => {
            const payload = action?.payload;
            if (payload) {
                state.tubingInput = {
                    ...payload,
                    casingODList: payload.casingODList && typeof payload.casingODList === 'object' && !Array.isArray(payload.casingODList)
                        ? transformObjectToKeyValueArray(payload.casingODList)
                        : payload.casingODList || [],
                    casingWeightList: payload.casingWeightList && typeof payload.casingWeightList === 'object' && !Array.isArray(payload.casingWeightList)
                        ? transformObjectToKeyValueArray(payload.casingWeightList)
                        : payload.casingWeightList || [],
                    linerODList: payload.linerODList && typeof payload.linerODList === 'object' && !Array.isArray(payload.linerODList)
                        ? transformObjectToKeyValueArray(payload.linerODList)
                        : payload.linerODList || [],
                    linerWeightList: payload.linerWeightList && typeof payload.linerWeightList === 'object' && !Array.isArray(payload.linerWeightList)
                        ? transformObjectToKeyValueArray(payload.linerWeightList)
                        : payload.linerWeightList || [],
                    tubingODList: payload.tubingODList && typeof payload.tubingODList === 'object' && !Array.isArray(payload.tubingODList)
                        ? transformObjectToKeyValueArray(payload.tubingODList)
                        : payload.tubingODList || [],
                    tubing1WeightList: payload.tubing1WeightList && typeof payload.tubing1WeightList === 'object' && !Array.isArray(payload.tubing1WeightList)
                        ? transformObjectToKeyValueArray(payload.tubing1WeightList)
                        : payload.tubing1WeightList || [],
                    tubing2WeightList: payload.tubing2WeightList && typeof payload.tubing2WeightList === 'object' && !Array.isArray(payload.tubing2WeightList)
                        ? transformObjectToKeyValueArray(payload.tubing2WeightList)
                        : payload.tubing2WeightList || [],
                    tubingTypeUpsetList: payload.tubingTypeUpsetList && typeof payload.tubingTypeUpsetList === 'object' && !Array.isArray(payload.tubingTypeUpsetList)
                        ? Object.keys(payload.tubingTypeUpsetList).map((key) => ({
                            value: payload.tubingTypeUpsetList[key],
                            label: key,
                        }))
                        : payload.tubingTypeUpsetList || [],
                    tubingTypeList: payload.tubingTypeList && typeof payload.tubingTypeList === 'object' && !Array.isArray(payload.tubingTypeList)
                        ? Object.keys(payload.tubingTypeList).map((key) => ({
                            value: payload.tubingTypeList[key],
                            label: key,
                        }))
                        : payload.tubingTypeList || [],
                    wellTrajectoryList: payload.wellTrajectoryList && typeof payload.wellTrajectoryList === 'object' && !Array.isArray(payload.wellTrajectoryList)
                        ? Object.keys(payload.wellTrajectoryList).map((key) => ({
                            value: payload.wellTrajectoryList[key],
                            label: key,
                        }))
                        : payload.wellTrajectoryList || [],
                };
            } else {
                state.tubingInput = null;
            }
        },
        setCasingODList: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    casingODList: Object?.keys(action?.payload)?.length > 0 ? transformObjectToKeyValueArray(action?.payload) : [],
                };
            }
        },
        setCasingOD(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    casingOD: +action.payload,
                    casingID: "",
                    casingDriftID: "",
                };
            }
        },
        setCasingWeight(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    casingWeight: +action.payload,
                };
            }
        },
        setCasingID(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    casingID: action.payload,
                };
            }
        },
        setCasingDriftID(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    casingDriftID: action.payload,
                };
            }
        },
        setLinerOD(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    linerOD: +action.payload,
                };
            }
        },
        setLinerWeight(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    linerWeight: action.payload,
                };
            }
        },
        setLinerID(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    linerID: action.payload,
                };
            }
        },
        setLinerTop(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    linerTop: action.payload,
                };
            }
        },
        setTubing1TubingOD(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing1_tubingOD: action.payload,
                    tubing1_tubingID: "",
                };
            }
        },
        setTubing1TubingID(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing1_tubingID: action.payload,
                };
            }
        },
        setTubing1TubingTypeUpset(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing1_tubingTypeUpset: action.payload,
                    tubing1_tubingID: "",
                };
            }
        },
        setTubing1TubingWeight(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing1_tubingWeight: action.payload,
                };
            }
        },
        setTubing1TubingType(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing1_tubingType: action.payload,
                };
            }
        },
        setTubing2TubingOD(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing2_tubingOD: action.payload,
                    tubing2_tubingID: "",
                };
            }
        },
        setTubing2TubingID(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing2_tubingID: action.payload,
                };
            }
        },
        setTubing2TubingTypeUpset(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing2_tubingTypeUpset: action.payload,
                    tubing2_tubingID: "",
                };
            }
        },
        setTubing2TubingWeight(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing2_tubingWeight: action.payload,
                };
            }
        },
        setTubing2TubingType(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing2_tubingType: action.payload,
                };
            }
        },
        setTubingTypeList(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubingTypeList: Object?.keys(action.payload)?.length > 0 ? Object.keys(action?.payload).map((key) => ({
                        value: action?.payload[key],
                        label: key,
                    })) : [],
                };
            }
        },
        setCasingWeightList(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    casingWeightList: Object?.keys(action?.payload)?.length > 0 ? transformObjectToKeyValueArray(action?.payload) : [],
                };
            }
        },
        setLinerODList(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    linerODList: Object?.keys(action?.payload)?.length > 0 ? transformObjectToKeyValueArray(action?.payload) : [],
                };
            }
        },
        setLinerWeightList(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    linerWeightList: Object?.keys(action?.payload)?.length > 0 ? transformObjectToKeyValueArray(action?.payload) : [],
                };
            }
        },
        setWellTrajectory(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    wellTrajectory: action.payload,
                };
            }
        },
        setWellTrajectoryList(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    wellTrajectoryList: Object?.keys(action.payload)?.length > 0 ? Object.keys(action?.payload).map((key) => ({
                        value: action?.payload[key],
                        label: key,
                    })) : [],
                };
            }
        },
        setTubingODList(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubingODList: Object?.keys(action?.payload)?.length > 0 ? transformObjectToKeyValueArray(action?.payload) : [],
                };
            }
        },
        setTubingTypeUpsetList(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubingTypeUpsetList: Object?.keys(action.payload)?.length > 0 ? Object.keys(action?.payload).map((key) => ({
                        value: action?.payload[key],
                        label: key,
                    })) : [],
                };
            }
        },
        setTubing1WeightList(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing1WeightList: Object?.keys(action?.payload)?.length > 0 ? transformObjectToKeyValueArray(action?.payload) : [],
                };
                const tubing1Weight = state?.tubingInput?.tubing1_tubingTypeUpset === "1" ? defaultTubingWeight?.find((tubing: any) => tubing?.value === state?.tubingInput?.tubing1_tubingOD)?.label : "";
                state.tubingInput.tubing1_tubingWeight = tubing1Weight ? tubing1Weight : state.tubingInput.tubing1_tubingWeight;
            }
        },
        setTubing2WeightList(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing2WeightList: Object?.keys(action?.payload)?.length > 0 ? transformObjectToKeyValueArray(action?.payload) : [],
                };
                const tubing2Weight = state?.tubingInput?.tubing2_tubingTypeUpset === "1" ? defaultTubingWeight?.find((tubing: any) => tubing?.value === state?.tubingInput?.tubing2_tubingOD)?.label : "";
                state.tubingInput.tubing2_tubingWeight = tubing2Weight ? tubing2Weight : state.tubingInput.tubing2_tubingWeight;
            }
        },
        setTubingInstallationType: (state, action) => {
            if (state?.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    tubingInstallationType: action?.payload,
                };
            };
        },
        setHiddenDesrdStg: (state, action) => {
            if (state?.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenDesrdStg: action?.payload,
                }
            };
        },
        setHiddenDesrdStg2: (state, action) => {
            if (state?.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenDesrdStg2: action?.payload,
                }
            }
        },
        setHiddenDesrdStg3: (state, action) => {
            if (state?.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenDesrdStg3: action?.payload,
                }
            };
        },
        setHiddenChargePumpSelected: (state, action) => {
            if (state?.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenChPmpSelcd: action?.payload,
                }
            };
        },
        setHiddenChargePumpSelected2: (state, action) => {
            if (state?.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenChPmp2Selcd: action?.payload,
                }
            };
        },
        setHiddenChargePumpSelected3: (state, action) => {
            if (state?.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenChPmp3Selcd: action?.payload,
                }
            };
        },
        setHiddenGasNatrAutoEff(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    // hiddenGasNatrEffAuto: action?.payload
                };
            }
        },
        setHiddenGasAutoEff(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    // hiddenGasAutoEff: action?.payload
                };
            }
        },
        setUnit(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    unitSys: action?.payload
                };
            }
        },
        setCatalogType(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    catalogType: action?.payload
                };
            }
            state.catalogType = action?.payload
        },
        setCatalogType2(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    catalogType: action?.payload
                };
            }
            state.catalogType = action?.payload;
        },
        setChargePumpShowAll(state, action) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    // chargePumpShowAll: action?.payload
                };
            }
        },
        setChargePumpShowAll2: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    // chargePumpShowAll: action?.payload
                };
            }
        },
        setHiddenSep1Eff: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    // hiddenSep1Eff: action?.payload
                };
            }
        },
        setHiddenSep2Eff: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    // hiddenSep2Eff: action?.payload
                };
            }
        },
        setHiddenSep3Eff: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    // hiddenSep3Eff: action?.payload
                };
            }
        },
        setHiddenSep4Eff: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    // hiddenSep4Eff: action?.payload
                };
            }
        },
        clearCasingOD(state) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    casingOD: "",
                    casingWeight: "",
                    casingWeightList: [],
                    casingID: "",
                    casingDriftID: "",
                    linerOD: "",
                    linerODList: [],
                    linerWeight: "",
                    linerWeightList: [],
                    linerID: "",
                };
            }
        },
        clearCasingWeight(state) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    casingWeight: state.tubingInput?.casingWeight || "",
                    casingID: "",
                    casingDriftID: "",
                };
            }
        },
        clearLiner(state) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    linerODList: [],
                    linerWeight: "",
                    linerWeightList: [],
                    linerID: "",
                    linerOD: "",
                };
            }
        },
        clearTubing1(state) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing1_tubingOD: "",
                    tubing1_tubingID: "",
                    tubing1_tubingWeight: state?.tubingInput?.tubing1_tubingWeight || "",
                    tubing1WeightList: [],
                };
            }
        },
        clearTubing2(state) {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state?.tubingInput,
                    tubing2_tubingOD: "",
                    tubing2_tubingID: "",
                    tubing2_tubingWeight: state?.tubingInput?.tubing2_tubingWeight || "",
                    tubing2WeightList: [],
                };
            }
        },
        setHiddenPumpId: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenPumpID: action?.payload,
                }
            };
        },
        setHiddenPumpShowAll: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnPmpShowAll: action?.payload,
                }
            };
        },
        setHiddenHousingType: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdn_Pump_HousingType: action?.payload,
                }
            };
        },
        setHiddenShaftType: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdn_Pump_ShaftType: action?.payload,
                }
            };
        },
        setHiddenProtectorBearingType: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnProtector_BearingType: action?.payload,
                }
            };
        },
        setHiddenProtector1: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnProtector_Protector1: action?.payload,
                }
            };
        },
        setHiddenProtector1HP: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnProtector_Protector1HP: action?.payload,
                }
            };
        },
        setHiddenProtector2: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnProtector_Protector2: action?.payload,
                }
            };
        },
        setHiddenProtector2HP: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnProtector_Protector2HP: action?.payload,
                }
            };
        },
        setHiddenProtectorShaftType: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnProtector_ShaftType: action?.payload,
                }
            };
        },
        setHiddenProtectorOilType: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnProtector_OilType: action?.payload,
                }
            };
        },
        setHiddenProtectorMotorHP: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenMotrHP: action?.payload,
                }
            };
        },
        setHiddenMotor60HP: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenMotr60HP: action?.payload,
                }
            };
        },
        setHiddenMotorId: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenMotrID: action?.payload,
                }
            };
        },
        setHiddenMotorSensor: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnMotr_Sensr: action?.payload,
                }
            };
        },
        setHiddenMotorShowAll: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnMotrShowAll: action?.payload,
                }
            };
        },
        setHiddenMotorAuto: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnMotrAutoHP: action?.payload,
                }
            };
        },
        setHiddenMotorSeries: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenMotrSers: action?.payload,
                }
            };
        },
        setHiddenMotorSharedOD: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenMotrShrdOD: action?.payload,
                }
            };
        },
        setHiddenMotorShrdWt: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenMotrShrdWght: action?.payload,
                }
            };
        },
        setHiddenMotorShrdRequired: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenWthrMotrShrd: action?.payload,
                }
            };
        },
        setHiddenMotorShrdRequiredInitialize: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenWthrMotrShrd: action?.payload,
                }
            };
        },
        setHiddenCableDriftId: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCable_IsDriftID: action?.payload,
                }
            };
        },
        setHiddenCablePumpLockPlate: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCable_PumpLockPlate: action?.payload,
                }
            };
        },
        setHiddenCableSealLockPlate: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCable_SealLockPlate: action?.payload,
                }
            };
        },
        setHiddenCableMotorLockPlate: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCable_MotorLockPlate: action?.payload,
                }
            };
        },
        setHiddenCableEstCabLng: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCable_EstCabLng: action?.payload,
                }
            };
        },
        setHiddenCableSurfaceCabl: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCable_SurfCabl: action?.payload,
                }
            };
        },
        setHiddenCableLockPlate: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCable_LockPlate: action?.payload,
                }
            };
        },
        setHiddenCableMotorLead: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCable_MotorLead: action?.payload,
                }
            };
        },
        setHiddenCableType: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCable_CableType: action?.payload,
                }
            };
        },
        setHiddenSurfacePowerFactor: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnSurface_PwrFactr: action?.payload,
                }
            };
        },
        setHiddenSurfaceEfficiency: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnSurface_Efficiency: action?.payload,
                }
            };
        },
        setHiddenSurfaceCstPrHr: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnSurface_CstPrHr: action?.payload,
                }
            };
        },
        setHiddenSurfaceSupplyVoltage: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnSurface_SuplVlt: action?.payload,
                }
            };
        },
        setHiddenSurfaceControlType: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hdnCntrlTyp: action?.payload,
                }
            };
        },
        setHiddenSurfaceControlLr: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // HdnSurf_Cntrlr: action?.payload,
                    // hiddenSurfVSD: action?.payload,
                }
            };
        },
        setHiddenSurfaceSwitchBoard: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenSwtchBrd: action?.payload,
                }
            }
        },
        setDummy: (state) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // dummy: true,
                }
            }
        },
        setHiddenSurfaceVSD: (state, action) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenSurfVSD: action?.payload,
                    // HdnSurf_Cntrlr: action?.payload,
                }
            }
        },
        resetHiddenMotorID: (state) => {
            if (state.tubingInput) {
                state.tubingInput = {
                    ...state.tubingInput,
                    // hiddenMotrID: '',
                    // hdnMotr_Sensr: -1
                }
            }
        }
    },
});

export const {
    resetTubingTab,
    setInputUnits,
    setTubingDetails,
    setCasingOD,
    setCasingWeight,
    setCasingID,
    setCasingDriftID,
    setLinerOD,
    setLinerWeight,
    setLinerID,
    setLinerTop,
    setTubing1TubingOD,
    setTubing1TubingID,
    setTubing1TubingTypeUpset,
    setTubing1TubingWeight,
    setTubing1TubingType,
    setTubing2TubingOD,
    setTubing2TubingID,
    setTubing2TubingTypeUpset,
    setTubing2TubingWeight,
    setTubing2TubingType,
    setCasingWeightList,
    setLinerODList,
    setLinerWeightList,
    setWellTrajectory,
    setWellTrajectoryList,
    setTubingODList,
    setTubingTypeUpsetList,
    setTubing1WeightList,
    setTubing2WeightList,
    setTubingInstallationType,
    clearCasingOD,
    clearCasingWeight,
    clearLiner,
    clearTubing1,
    clearTubing2,
    setTubingTypeList,
    setHiddenDesrdStg,
    setHiddenDesrdStg2,
    setHiddenDesrdStg3,
    setHiddenChargePumpSelected,
    setHiddenChargePumpSelected2,
    setHiddenChargePumpSelected3,
    setHiddenGasNatrAutoEff,
    setHiddenGasAutoEff,
    setUnit,
    setCatalogType,
    setCatalogType2,
    setChargePumpShowAll,
    setChargePumpShowAll2,
    setHiddenSep1Eff,
    setHiddenSep2Eff,
    setHiddenSep3Eff,
    setHiddenSep4Eff,
    setCasingODList,
    setHiddenPumpId,
    setHiddenPumpShowAll,
    setHiddenCableDriftId,
    setHiddenCableEstCabLng,
    setHiddenCableLockPlate,
    setHiddenCableMotorLead,
    setHiddenCableMotorLockPlate,
    setHiddenCablePumpLockPlate,
    setHiddenCableSealLockPlate,
    setHiddenCableSurfaceCabl,
    setHiddenCableType,
    setHiddenHousingType,
    setHiddenMotor60HP,
    setHiddenMotorAuto,
    setHiddenMotorId,
    setHiddenMotorSensor,
    setHiddenMotorShowAll,
    setHiddenMotorSeries,
    setHiddenMotorSharedOD,
    setHiddenMotorShrdRequired,
    setHiddenMotorShrdRequiredInitialize,
    setHiddenMotorShrdWt,
    setHiddenProtector1,
    setHiddenProtector1HP,
    setHiddenProtector2,
    setHiddenProtector2HP,
    setHiddenProtectorBearingType,
    setHiddenProtectorMotorHP,
    setHiddenProtectorOilType,
    setHiddenProtectorShaftType,
    setHiddenShaftType,
    setHiddenSurfaceControlLr,
    setHiddenSurfaceControlType,
    setHiddenSurfaceCstPrHr,
    setHiddenSurfaceEfficiency,
    setHiddenSurfacePowerFactor,
    setHiddenSurfaceSupplyVoltage,
    setHiddenSurfaceSwitchBoard,
    setDummy,
    setHiddenSurfaceVSD,
    resetHiddenMotorID,
} = TubingSlice.actions;

export default TubingSlice.reducer;
